import { Box, Collapse, Divider, Typography } from "@mui/material"
import { WarningOutlined } from "@mui-symbols-material/w300"
import type { FC } from "react"
import { Fragment, useState } from "react"
import { useFieldArray, useFormContext, useWatch } from "react-hook-form"

import { CustomerPurchaseOrderDeliveryDateLineRow } from "./CustomerPurchaseOrderDeliveryDateLineRow"
import { CustomerPurchaseOrderLineItemFooter } from "./CustomerPurchaseOrderLineItemFooter"
import { CustomerPurchaseOrderLineItemHeader } from "./CustomerPurchaseOrderLineItemHeader"

import ConfirmationDialog from "@/components/common/Modals/ConfirmationDialog"
import type {
  CompanyPreferredMeasurementSystemEnum,
  CustomerPurchaseOrderPricingTotalsProductResult,
} from "@/graphql/codegen/graphql"
import type {
  CpoDeliveryDateLineFormValues,
  CustomerPurchaseOrderLineItemsFormValues,
} from "@/screens/CustomerOrders/schemas"
import { calculateLineItemOverTotals, getCpoDeliveryDateLineDefaultFormValues } from "@/screens/CustomerOrders/utils"

interface CustomerPurchaseOrderLineItemCardProps {
  lineItemIndex: number
  preferredShipFromLocationId: string | null
  preferredMeasurementSystem: CompanyPreferredMeasurementSystemEnum | undefined
  productResult: CustomerPurchaseOrderPricingTotalsProductResult | undefined
  isPricingFetching: boolean
  shipToLocationId: string
  onDelete: () => void
}

export const CustomerPurchaseOrderLineItemCard: FC<CustomerPurchaseOrderLineItemCardProps> = ({
  lineItemIndex,
  preferredShipFromLocationId,
  preferredMeasurementSystem,
  productResult,
  isPricingFetching,
  shipToLocationId,
  onDelete,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const [deletingDeliveryDateLineIndex, setDeletingDeliveryDateLineIndex] = useState<number | undefined>()

  const { control, setValue } = useFormContext<CustomerPurchaseOrderLineItemsFormValues>()
  const lineItem = useWatch({ control, name: `lineItems.${lineItemIndex}` })

  const overTotals = calculateLineItemOverTotals(lineItem, productResult?.total)
  const hasAnyOverTotal = overTotals.isPalletsOverTotal || overTotals.isUnitsOverTotal || overTotals.isUomOverTotal

  const {
    fields: deliveryDateLineFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `lineItems.${lineItemIndex}.deliveryDateLines` as `lineItems.${number}.deliveryDateLines`,
  })

  const handleDeliveryDateAdd = () => {
    append(
      getCpoDeliveryDateLineDefaultFormValues(
        lineItem.cpoLineItemId,
        { preferredShipFromLocationId, preferredMeasurementSystem },
        { packageUsWeightUom: lineItem.packageUsWeightUom, packageMetricWeightUom: lineItem.packageMetricWeightUom }
      )
    )
  }

  // the delivery-date line's own trash-can removes just that line —
  // unless it's the product's only remaining line, in which case removing it removes the whole
  // product line item (and its child lines), since a product with zero delivery-date lines has
  // no reason to still show. Both are staged, unsaved edits — Discard reverts either.
  const removeDeliveryDateLine = (deliveryDateLineIndex: number) => {
    if (deliveryDateLineFields.length === 1) {
      onDelete()
      return
    }
    remove(deliveryDateLineIndex)
  }

  // A line with an assigned soNumber represents a real, already-created Sales Order — deleting it
  // (or the whole product, via the "last line" branch above) needs an explicit confirm. A still-draft
  // line (soNumber === null) deletes immediately, same as before.
  const handleDeliveryDateDelete = (deliveryDateLineIndex: number) => {
    const targetLine = lineItem.deliveryDateLines[deliveryDateLineIndex]
    if (targetLine?.soNumber) {
      setDeletingDeliveryDateLineIndex(deliveryDateLineIndex)
      return
    }
    removeDeliveryDateLine(deliveryDateLineIndex)
  }

  const handleDeliveryDateDeleteConfirm = () => {
    if (deletingDeliveryDateLineIndex === undefined) return
    removeDeliveryDateLine(deletingDeliveryDateLineIndex)
    setDeletingDeliveryDateLineIndex(undefined)
  }

  const handlePriceOverrideSave = (deliveryDateLineIndex: number, updates: Partial<CpoDeliveryDateLineFormValues>) => {
    const path = `lineItems.${lineItemIndex}.deliveryDateLines.${deliveryDateLineIndex}` as const

    if (updates.pricePerUom !== undefined) {
      setValue(`${path}.pricePerUom`, updates.pricePerUom, { shouldDirty: true })
    }
    if (updates.isManualPriceSet !== undefined) {
      setValue(`${path}.isManualPriceSet`, updates.isManualPriceSet, { shouldDirty: true })
    }
  }

  return (
    <Box
      className='overflow-hidden rounded-md border border-solid border-gray-400'
      data-testid={`customer-orders.line-item-card.${lineItem.cpoLineItemId}`}
    >
      <CustomerPurchaseOrderLineItemHeader
        lineItemIndex={lineItemIndex}
        converted={productResult?.converted}
        isExpanded={isExpanded}
        onToggle={() => setIsExpanded((prev) => !prev)}
      />

      <Collapse in={isExpanded}>
        {deliveryDateLineFields.map((field, deliveryDateLineIndex) => (
          <Fragment key={field.id}>
            {deliveryDateLineIndex > 0 && <Divider />}
            <CustomerPurchaseOrderDeliveryDateLineRow
              lineItemIndex={lineItemIndex}
              deliveryDateLineIndex={deliveryDateLineIndex}
              shipToLocationId={shipToLocationId}
              packageUsWeightUom={lineItem.packageUsWeightUom}
              packageMetricWeightUom={lineItem.packageMetricWeightUom}
              packageConversion={lineItem}
              isPalletsOverTotal={overTotals.isPalletsOverTotal}
              isUnitsOverTotal={overTotals.isUnitsOverTotal}
              isUomOverTotal={overTotals.isUomOverTotal}
              lineTotals={productResult?.lines[deliveryDateLineIndex]}
              isPricingFetching={isPricingFetching}
              onDelete={() => handleDeliveryDateDelete(deliveryDateLineIndex)}
              onPriceOverrideSave={(updates) => handlePriceOverrideSave(deliveryDateLineIndex, updates)}
            />
          </Fragment>
        ))}

        <CustomerPurchaseOrderLineItemFooter
          overTotals={overTotals}
          productTotal={productResult?.total}
          pallets={lineItem.pallets}
          units={lineItem.units}
          uom={lineItem.uom}
          quantity={lineItem.quantity}
          cpoLineItemId={lineItem.cpoLineItemId}
          isPricingFetching={isPricingFetching}
          onDeliveryDateAdd={handleDeliveryDateAdd}
        />

        {hasAnyOverTotal && (
          <Box className='flex justify-end px-6 pb-6'>
            <Box
              className='inline-flex items-center gap-2 rounded-md border border-solid border-tertiary-yellow-300 bg-tertiary-yellow-100 px-4 py-3 text-tertiary-yellow-900'
              data-testid={`customer-orders.line-item-card.over-total-warning-${lineItem.cpoLineItemId}`}
            >
              <WarningOutlined fontSize='small' />
              <Typography variant='body2' className='whitespace-nowrap text-base'>
                Your sales orders list more products than your PO summary. Review your releases if this seems incorrect.
              </Typography>
            </Box>
          </Box>
        )}
      </Collapse>

      {deletingDeliveryDateLineIndex !== undefined && (
        <ConfirmationDialog
          title='Are you sure you want to delete this sales order?'
          content='This sales order has already been assigned a number. Deleting it cannot be undone.'
          closeLabel='Cancel'
          confirmLabel='Delete'
          isWarning
          onClose={() => setDeletingDeliveryDateLineIndex(undefined)}
          onConfirm={handleDeliveryDateDeleteConfirm}
        />
      )}
    </Box>
  )
}
